import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import generateClassesFromMap from '@insight/toolkit-utils/lib/generators/generateClassesFromMap';
import generateSecureRel from '@insight/toolkit-utils/lib/generators/generateSecureRel';

import { generateButtonContent, generateButtonIcon } from './helpers';
import { BUTTON_COLORS, BUTTON_SIZES, BUTTON_CLASS_MAP } from './classes';

const Button = ({
  anchorComponent,
  buttonRef,
  children,
  className,
  color,
  href,
  icon,
  isDisabled,
  isInvisible,
  iconPosition,
  fullWidth,
  isLoading,
  rel,
  size,
  target,
  type,
  buttonClassName,
  ...rest
}) => {
  // Force disabled state on loading
  // eslint-disable-next-line no-param-reassign
  isDisabled = isDisabled || isLoading;
  const buttonIcon = generateButtonIcon({ isLoading, icon, iconPosition });
  const content = generateButtonContent({
    buttonIcon,
    children,
    iconPosition,
    buttonClassName,
  });
  const derivedClasses = generateClassesFromMap(
    { color, size },
    BUTTON_CLASS_MAP
  );
  const classes = cn(
    'c-button',
    derivedClasses,
    {
      'has-icon': !!icon,
      'is-disabled': isDisabled,
      'u-invisible': isInvisible,
      'c-button--block': fullWidth,
    },
    className
  );

  if (href) {
    const secureRel = generateSecureRel(target, rel);
    const Anchor = anchorComponent || 'a';

    return (
      <Anchor
        className={classes}
        href={href}
        ref={buttonRef}
        rel={secureRel}
        target={target}
        {...rest}
      >
        {content}
      </Anchor>
    );
  }
  return (
    <button
      className={classes}
      disabled={isDisabled}
      type={type}
      ref={buttonRef}
      {...rest}
    >
      {content}
    </button>
  );
};

export default Button;

/* eslint-disable react/require-default-props */
Button.propTypes = {
  /** An alternative component to use for anchor elements */
  anchorComponent: PropTypes.func,
  buttonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf(BUTTON_COLORS),
  href: PropTypes.string,
  icon: PropTypes.string,
  iconPosition: PropTypes.string,
  /** Disables the button, changing the visual style and make it unable to be pressed */
  isDisabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  isLoading: PropTypes.bool,
  rel: PropTypes.string,
  size: PropTypes.oneOf(BUTTON_SIZES),
  target: PropTypes.string,
  type: PropTypes.string,
  buttonClassName: PropTypes.string,
};

Button.defaultProps = {
  anchorComponent: null,
  children: null,
  className: '',
  color: null,
  fullWidth: false,
  isDisabled: false,
  isLoading: false,
  type: 'button',
  iconPosition: 'left',
  buttonClassName: '',
};
/* eslint-enable react/require-default-props */
